import { Controller } from "@hotwired/stimulus"

// A minimal accessible modal: the trigger lives inside the controller's scope
// and the overlay is a plain `div` toggled with the `hidden` attribute.
//
// Deliberately not `<dialog>`: Safari 15.0–15.3 has no support for it and that
// is the browser floor this app builds for (see esbuild target / allow_browser),
// so `showModal()` would silently do nothing and leave the form unreachable.
//
// What it does provide: Escape closes, a click on the backdrop closes, focus
// moves into the dialog on open, stays inside it while it is open, and returns
// to whatever opened it on close.
export default class extends Controller {
  static targets = ["dialog"]

  static FOCUSABLE = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled]):not([type=hidden])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[tabindex]:not([tabindex='-1'])"
  ].join(", ")

  connect() {
    this.keydownHandler = (event) => this.handleKeydown(event)
    this.beforeCacheHandler = () => this.close()

    // Turbo caches the page exactly as it stands — open dialog, and the
    // `modal-open` class on <body> with it — and puts that snapshot back on
    // Back/Forward. The restored markup gets a *new* controller instance, and
    // the keydown listener that makes Escape and the focus trap work is only
    // ever attached in open(): the customer would come back to a modal that
    // cannot be closed with the keyboard, over a page that cannot scroll.
    // Snapshotting it closed keeps the two in step, whichever way the visit is
    // rendered.
    document.addEventListener("turbo:before-cache", this.beforeCacheHandler)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.beforeCacheHandler)
    this.releaseDocument()
    this.opener = null
  }

  open(event) {
    if (event) event.preventDefault()
    if (!this.hasDialogTarget || this.isOpen) return
    if (!this.triggerFormIsValid(event)) return

    this.opener = document.activeElement
    this.dialogTarget.hidden = false
    document.addEventListener("keydown", this.keydownHandler)
    document.body.classList.add("modal-open")

    const focusable = this.focusableElements()
    if (focusable.length > 0) focusable[0].focus()
  }

  close(event) {
    if (event) event.preventDefault()
    if (!this.hasDialogTarget || !this.isOpen) return

    this.dialogTarget.hidden = true
    this.releaseDocument()

    // Give focus back to the control that opened the modal, so keyboard users
    // are not dropped at the top of the document.
    if (this.opener && document.contains(this.opener)) this.opener.focus()
    this.opener = null
  }

  // A trigger marked `data-modal-validate="form"` stands in for a submit button
  // of the form it lives in — the dialog only asks *how* to submit it. So the
  // form has to be filled in before the dialog covers it: otherwise the browser
  // would point its validation bubble at a field hidden behind the backdrop,
  // and the button in the dialog would look broken.
  triggerFormIsValid(event) {
    const trigger = event && event.currentTarget
    if (!trigger || trigger.dataset.modalValidate !== "form" || !trigger.form) return true

    return trigger.form.reportValidity()
  }

  get isOpen() {
    return this.hasDialogTarget && !this.dialogTarget.hidden
  }

  handleKeydown(event) {
    if (event.key === "Escape" || event.key === "Esc") {
      this.close(event)
    } else if (event.key === "Tab") {
      this.trapFocus(event)
    }
  }

  // Keep Tab cycling inside the dialog while it is open — without it the focus
  // ring walks off into the page behind the backdrop.
  //
  // The first case is the one that matters most: focus is not inside the dialog
  // at all. That happens routinely — a click on the backdrop moves focus to the
  // body, and so does coming back from the address bar — and until it was
  // handled here a plain Tab from there walked straight into the page behind
  // the modal, which is exactly what a focus trap exists to prevent.
  trapFocus(event) {
    const focusable = this.focusableElements()
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const active = document.activeElement

    if (!this.dialogTarget.contains(active)) {
      event.preventDefault()
      ;(event.shiftKey ? last : first).focus()
    } else if (event.shiftKey && active === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && active === last) {
      event.preventDefault()
      first.focus()
    }
  }

  focusableElements() {
    if (!this.hasDialogTarget) return []

    return Array.from(this.dialogTarget.querySelectorAll(this.constructor.FOCUSABLE))
      .filter((element) => element.offsetParent !== null || element === document.activeElement)
  }

  releaseDocument() {
    document.removeEventListener("keydown", this.keydownHandler)
    document.body.classList.remove("modal-open")
  }
}
